import { HeadData } from '@/components/HeadData';
import { type FragmentType, useFragment } from '@/gql/codegen';
import { graphql } from '@/gql/codegen/gql';
import { StartPage } from '@/routes/_embaUserLayout/-components/StartPage';
import { generateScenarioFileUrl } from '@/utils/generateScenarioFileUrl';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CourseLessonScenarioPageComponent } from '..';
import { Background } from '../-components/Background';
import { useImagePreloader } from './-hooks/useImagePreloader';
export const COURSE_LESSON_SCENARIO_FRAGMENT = graphql(/* GraphQL */ `
  fragment EmbaCourseLessonScenario on CourseLessonScenario {
    id
    story {
      id
      ...EmbaCourseLessonScenarioStoryContainer
      ...EmbaCourseLessonScenarioStory
      ...EmbaCourseLessonScenarioStoryPreload
    }
  }
`);

export const COURSE_LESSON_SCENARIO_STORY_FOR_CONTAINER_FRAGMENT =
  graphql(/* GraphQL */ `
  fragment EmbaCourseLessonScenarioStoryContainer on CourseLessonScenarioStory {
    id
    nextId
    prevId
    background
    content {
      __typename
    }
  }
`);

export type CourseLessonScenarioContainerProps = {
  embaUserlastName: string;
  content: FragmentType<typeof COURSE_LESSON_SCENARIO_FRAGMENT>;
  lessonName: string;
  chapterName: string;
  onEmbaStartCourseLesson: () => Promise<void>;
  onEmbaCompleteCourseLesson: () => Promise<void>;
  headTitle: string;
};

export const CourseLessonScenarioContainer = ({
  embaUserlastName,
  content,
  lessonName,
  chapterName,
  onEmbaStartCourseLesson,
  onEmbaCompleteCourseLesson,
  headTitle,
}: CourseLessonScenarioContainerProps) => {
  const embaCourseLessonScenarioData = useFragment(
    COURSE_LESSON_SCENARIO_FRAGMENT,
    content,
  );

  const storyData = embaCourseLessonScenarioData.story;

  const [processState, setProcessState] = useState<'start' | 'in progress'>(
    'start',
  );

  const [currentStoryId, setCurrentStoryId] = useState<number>(1);

  const currentStory = useMemo(() => {
    const story = storyData.find((story) => story.id === currentStoryId);
    if (!story) {
      throw new Error(`Story not found (currentStoryId: ${currentStoryId})`);
    }

    return story;
  }, [storyData, currentStoryId]);

  const currentStoryData = useFragment(
    COURSE_LESSON_SCENARIO_STORY_FOR_CONTAINER_FRAGMENT,
    currentStory,
  );

  const [typingSpeed, setTypingSpeed] = useState<number>(20);
  const [showBlinkingArrow, setShowBlinkingArrow] = useState<boolean>(false);
  const [showInteractions, setShowInteractions] = useState<boolean>(false);

  const [showQuiz, setShowQuiz] = useState<boolean>(false);
  const [isQuizCorrect, setIsQuizCorrect] = useState<boolean | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);

  // ストーリーデータから画像をプリロード
  useImagePreloader(storyData);

  const animationComplete = () => {
    setShowInteractions(true);
    setShowBlinkingArrow(true);
  };

  const scenarioFinished = useCallback(async () => {
    await onEmbaCompleteCourseLesson();
  }, [onEmbaCompleteCourseLesson]);

  const isEnableNext: boolean = useMemo<boolean>(() => {
    if (isQuizCorrect) {
      return true;
    }
    if (
      currentStoryData.content.__typename === 'ScenarioContentChoice' ||
      currentStoryData.content.__typename === 'ScenarioContentQuiz'
    ) {
      return false;
    }
    return true;
  }, [currentStoryData, isQuizCorrect]);

  const isEnablePrev: boolean = useMemo<boolean>(() => {
    return currentStoryId > 1;
  }, [currentStoryId]);

  const startLesson = () => {
    onEmbaStartCourseLesson().catch(() => {});
    setProcessState('in progress');
  };

  const refreshScenarioState = useCallback(() => {
    setShowInteractions(false);
    setIsQuizCorrect(null);
    setShowQuiz(false);
    setTypingSpeed(10);
    setShowBlinkingArrow(false);
  }, []);

  const slideToNext = useCallback(() => {
    if (!isEnableNext) return;
    if (currentStoryId === storyData.length) {
      scenarioFinished().catch(() => {});
      return;
    }

    if (currentStoryData.nextId) {
      refreshScenarioState();
      setCurrentStoryId(currentStoryData.nextId);
    }
  }, [
    currentStoryData,
    currentStoryId,
    isEnableNext,
    scenarioFinished,
    storyData,
    refreshScenarioState,
  ]);

  const slideToPrev = useCallback(() => {
    if (!isEnablePrev) return;

    if (currentStoryData.prevId) {
      refreshScenarioState();
      setCurrentStoryId(currentStoryData.prevId);
    }
  }, [currentStoryData, isEnablePrev, refreshScenarioState]);

  const slideToHome = useCallback(() => {
    if (currentStoryId === 1) return;

    refreshScenarioState();
    setCurrentStoryId(1);
  }, [refreshScenarioState, currentStoryId]);

  const quizModalClose = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    if (e.target === e.currentTarget) {
      setShowQuiz(false);
    }
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTypingSpeed(0);
    setShowInteractions(true);
    setCurrentStoryId(Number(e.target.value));
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (processState === 'start') {
        return;
      }

      if (isEnableNext && (event.key === ' ' || event.key === 'ArrowRight')) {
        slideToNext();
        event.preventDefault();
      } else if (event.key === 'ArrowLeft') {
        slideToPrev();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEnableNext, slideToNext, slideToPrev, processState]);

  // インタラクション系の関数
  const handleChoiceClick = (isCorrectChoice: boolean) => {
    setIsQuizCorrect(isCorrectChoice);
  };

  useEffect(() => {
    if (sliderRef.current !== null) {
      const percentage = ((currentStoryId - 1) / (storyData.length - 1)) * 100;
      sliderRef.current.style.background = `linear-gradient(to right, #7EC718 ${percentage}%, #fff ${percentage}%)`;
    }
  }, [currentStoryId, storyData]);

  return (
    // レッスン開始時の画面ちらつき防止
    <Background
      src={generateScenarioFileUrl(
        currentStoryData.background && currentStoryId === 1
          ? currentStoryData.background
          : '',
        'background',
      )}
    >
      <HeadData title={headTitle} />

      {processState === 'start' && (
        <StartPage
          onClick={startLesson}
          chapterName={chapterName}
          lessonName={lessonName}
          lessonType="scenario"
          showBackgroundImage={false}
        />
      )}
      {processState === 'in progress' && (
        <CourseLessonScenarioPageComponent
          currentStory={currentStory}
          chapterName={chapterName}
          lessonName={lessonName}
          embaUserlastName={embaUserlastName}
          isEnableNext={isEnableNext}
          isEnablePrev={isEnablePrev}
          animationComplete={animationComplete}
          typingSpeed={typingSpeed}
          showBlinkingArrow={showBlinkingArrow}
          showInteractions={showInteractions}
          showQuiz={showQuiz}
          isModalVisible={isModalVisible}
          slideToHome={slideToHome}
          quizModalClose={quizModalClose}
          handleSliderChange={handleSliderChange}
          handleChoiceClick={handleChoiceClick}
          storyDataLength={storyData.length}
          currentStoryId={currentStoryId}
          sliderRef={sliderRef}
          slideToNext={slideToNext}
          slideToPrev={slideToPrev}
          setCurrentStoryId={setCurrentStoryId}
          setShowQuiz={setShowQuiz}
          setIsModalVisible={setIsModalVisible}
          setShowInteractions={setShowInteractions}
          isQuizCorrect={isQuizCorrect}
          setIsQuizCorrect={setIsQuizCorrect}
        />
      )}
    </Background>
  );
};
